import React, { useState, useEffect } from 'react';
import {
  ShieldCheck,
  ChevronDown,
  ChevronUp,
  Lock,
  AlertTriangle,
  User,
  Clock,
  Building2
} from 'lucide-react';

interface BillingOperationalEvidencePanelProps {
  selectedDoc: any;
  userRole?: string;
  canViewDetailedEvidence?: boolean;
  canonicalBillingIdentity?: any;
  className?: string;
  lightTheme?: boolean;
}

export const BillingOperationalEvidencePanel: React.FC<BillingOperationalEvidencePanelProps> = ({
  selectedDoc,
  userRole,
  canViewDetailedEvidence,
  canonicalBillingIdentity: propBillingIdentity,
  className = '',
  lightTheme = false
}) => {
  const [showIdentityDrawer, setShowIdentityDrawer] = useState(false);
  const [fetchedBillingIdentity, setFetchedBillingIdentity] = useState<any>(null);

  useEffect(() => {
    if (!propBillingIdentity) {
      fetch('/api/connections')
        .then(res => (res.ok ? res.json() : null))
        .then(data => {
          if (data?.billingIdentity) {
            setFetchedBillingIdentity(data.billingIdentity);
          }
        })
        .catch(err => console.error('Error fetching billing identity:', err));
    }
  }, [propBillingIdentity]);

  const canonicalBillingIdentity = propBillingIdentity || fetchedBillingIdentity;

  const isOperationalUser =
    canViewDetailedEvidence ??
    (userRole === 'OWNER' ||
      userRole === 'REFERRAL_COORDINATOR' ||
      userRole === 'ADMIN' ||
      userRole === 'OFFICE' ||
      userRole === 'BILLING');

  const formatConciseDateTime = (dateStr?: string | null): string => {
    if (!dateStr) return 'N/A';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  if (!selectedDoc) return null;

  const snapshot = selectedDoc.finalized_snapshot || null;
  const isFrozen = Boolean(selectedDoc.is_frozen_snapshot || snapshot);
  const bi = snapshot?.organization_billing_identity || canonicalBillingIdentity;

  const orgName = bi?.company_name || 'Not Configured — Admin Setup Required';
  const legalEntityName =
    bi?.legal_entity_name || bi?.legalEntityName || bi?.company_name || 'Not Configured — Admin Setup Required';
  const orgNpi = bi?.skilled_npi || bi?.npi || bi?.organizationNpi || 'Not Configured — Admin Setup Required';
  const orgTaxId = bi?.tax_id || bi?.taxId || 'Not Configured — Admin Setup Required';
  const orgTaxonomy = bi?.taxonomy_code || bi?.taxonomyCode || 'Not Configured — Admin Setup Required';

  let orgAddress = 'Not Configured — Admin Setup Required';
  if (bi?.address) {
    orgAddress = bi.address;
  } else if (bi?.billing_contact_address) {
    const parts = [
      bi.billing_contact_address,
      bi.city,
      [bi.state, bi.zip].filter(Boolean).join(' ')
    ].filter(Boolean);
    if (parts.length > 0) orgAddress = parts.join(', ');
  }

  const provName = snapshot?.treating_provider?.name || selectedDoc.provider_name || 'Assigned Clinician';
  const provTitle =
    snapshot?.treating_provider?.title ||
    selectedDoc.provider_title ||
    selectedDoc.provider_role ||
    'Not Configured — Admin Setup Required';
  const provRole = snapshot?.treating_provider?.role || selectedDoc.provider_role || 'SLP';
  const provNpi =
    snapshot?.treating_provider?.npi || selectedDoc.provider_npi || 'Not Configured — Admin Setup Required';
  const provLicense =
    snapshot?.treating_provider?.license_number ||
    selectedDoc.provider_license ||
    'Not Configured — Admin Setup Required';

  const supName = snapshot?.supervisor?.name || selectedDoc.supervisor_name || 'Not Configured — Admin Setup Required';
  const supTitle = snapshot?.supervisor?.title || selectedDoc.supervisor_title || 'MS, CCC-SLP';
  const supNpi =
    snapshot?.supervisor?.npi || selectedDoc.supervisor_npi || 'Not Configured — Admin Setup Required';
  const supLicense =
    snapshot?.supervisor?.license_number ||
    selectedDoc.supervisor_license ||
    'Not Configured — Admin Setup Required';

  const isSelectedDocSlpa = selectedDoc.provider_role === 'SLPA';

  const readinessStatus =
    selectedDoc.billing_readiness?.status ||
    selectedDoc.readiness_status ||
    (selectedDoc.is_cleared ? 'BILLING_READY' : 'HELD');
  const isReady = readinessStatus === 'BILLING_READY' || readinessStatus === 'READY';
  const isExcluded = readinessStatus === 'EXCLUDED';
  const holdReasons: string[] =
    selectedDoc.billing_readiness?.hold_reasons ||
    (selectedDoc.hold_reasons_json
      ? (() => {
          try {
            return JSON.parse(selectedDoc.hold_reasons_json);
          } catch {
            return [];
          }
        })()
      : []);

  const targetRoute =
    selectedDoc.billing_readiness?.billing_route === 'CLAIMMD_COMMERCIAL'
      ? 'Claim.MD Commercial 837'
      : selectedDoc.billing_readiness?.billing_route === 'CLAIMMD_PRIMARY_DDD_SECONDARY'
      ? 'Claim.MD Primary + DDD Secondary'
      : 'WellSky DDD 837';

  // Supervisor or non-operational role: show ONLY concise readiness state
  if (!isOperationalUser) {
    if (lightTheme) {
      return (
        <div className={`p-2.5 rounded-xl bg-slate-50 border border-slate-200 flex items-center justify-between text-xs shadow-xs ${className}`}>
          <div className="flex items-center space-x-2">
            <ShieldCheck className={`w-3.5 h-3.5 ${isReady ? 'text-emerald-600' : isExcluded ? 'text-slate-400' : 'text-amber-600'}`} />
            <span className="text-slate-600 font-medium">Billing Readiness:</span>
            <span
              className={`font-semibold ${
                isReady ? 'text-emerald-700' : isExcluded ? 'text-slate-500' : 'text-amber-700'
              }`}
            >
              {isReady ? 'Ready for Claim Generation' : isExcluded ? 'Excluded' : 'Held for Review'}
            </span>
          </div>
          {holdReasons.length > 0 && !isReady && (
            <span className="text-[10px] text-amber-700 font-mono">
              {holdReasons.length} {holdReasons.length === 1 ? 'item' : 'items'} pending
            </span>
          )}
        </div>
      );
    }

    return (
      <div className={`p-2.5 rounded-xl bg-[#0e1626]/85 border border-slate-700/70 flex items-center justify-between text-xs shadow-xs ${className}`}>
        <div className="flex items-center space-x-2">
          <ShieldCheck className={`w-3.5 h-3.5 ${isReady ? 'text-emerald-400' : isExcluded ? 'text-slate-400' : 'text-amber-400'}`} />
          <span className="text-slate-300 font-medium">Billing Readiness:</span>
          <span
            className={`font-semibold ${
              isReady ? 'text-emerald-300' : isExcluded ? 'text-slate-400' : 'text-amber-300'
            }`}
          >
            {isReady ? 'Ready for Claim Generation' : isExcluded ? 'Excluded' : 'Held for Review'}
          </span>
        </div>
        {holdReasons.length > 0 && !isReady && (
          <span className="text-[10px] text-amber-300/80 font-mono">
            {holdReasons.length} {holdReasons.length === 1 ? 'item' : 'items'} pending
          </span>
        )}
      </div>
    );
  }

  // Operational / Admin roles (Owner, Referral Coordinator, Admin, Office, Billing):
  // Full expandable Billing & Operational Identity Evidence Panel
  return (
    <div className={`space-y-2 ${className}`}>
      <button
        type="button"
        onClick={() => setShowIdentityDrawer(!showIdentityDrawer)}
        className="w-full flex items-center justify-between p-2.5 rounded-xl bg-[#0e1626]/85 hover:bg-[#131d30] border border-slate-700/70 hover:border-slate-600/70 text-slate-300 text-xs font-semibold shadow-xs transition-colors"
      >
        <div className="flex items-center space-x-2">
          <ShieldCheck className="w-3.5 h-3.5 text-purple-300/80" />
          <span>Billing & Operational Identity Evidence</span>
          <span className="text-[10px] text-slate-500 font-mono hidden sm:inline">
            (NPI, Auth, Modality & Rendering Hierarchy)
          </span>
        </div>
        <div className="flex items-center space-x-1 text-slate-400 text-[11px]">
          <span>{showIdentityDrawer ? 'Collapse' : 'Expand Details'}</span>
          {showIdentityDrawer ? (
            <ChevronUp className="w-3.5 h-3.5 text-slate-400" />
          ) : (
            <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
          )}
        </div>
      </button>

      {showIdentityDrawer && (
        <div className="p-4 rounded-xl bg-gradient-to-b from-[#0c1424]/90 to-[#080d18]/95 border border-slate-700/70 shadow-[0_2px_8px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(255,255,255,0.04)] space-y-4 text-xs font-mono animate-in fade-in duration-150">
          {/* Snapshot Status Banner */}
          {isFrozen ? (
            <div className="p-2.5 rounded-lg bg-emerald-950/40 border border-emerald-800/60 text-emerald-300 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center space-x-2">
                <Lock className="w-4 h-4 text-emerald-400 shrink-0" />
                <span className="font-bold tracking-wide">[FROZEN LEGAL RECORD] Finalized Snapshot</span>
              </div>
              <span className="text-[11px] text-emerald-400/90 font-mono">
                Captured at: {formatConciseDateTime(snapshot?.finalized_at || selectedDoc.finalized_at)} (Historic evidence frozen against future credential updates)
              </span>
            </div>
          ) : (
            <div className="p-2.5 rounded-lg bg-amber-950/30 border border-amber-800/50 text-amber-300 flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center space-x-2">
                <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0" />
                <span className="font-bold tracking-wide">[LIVE REGISTRY FALLBACK] Active Registry Mode</span>
              </div>
              <span className="text-[11px] text-amber-400/90 font-mono">
                Using current provider/supervisor registry values (Unfinalized Document)
              </span>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Client Identity & Authorization Linkage Block */}
            <div className="p-3 rounded-lg bg-[#0e1626]/85 border border-slate-700/70 space-y-1.5">
              <div className="text-[10px] font-bold text-sky-400 uppercase tracking-wider flex items-center space-x-1">
                <User className="w-3 h-3" />
                <span>Client Identity & Authorization Linkage</span>
              </div>
              <div className="text-white font-sans font-semibold text-xs">
                {selectedDoc.first_name} {selectedDoc.last_name}
              </div>
              <div className="text-slate-400 text-[11px] space-y-0.5">
                <div>Client ID: <span className="text-slate-200">{selectedDoc.client_id}</span></div>
                <div>AHCCCS / Member ID: <span className="text-slate-200">{selectedDoc.ahcccs_id || selectedDoc.policy_id || 'N/A'}</span></div>
                <div>Primary Payer: <span className="text-slate-200">{selectedDoc.primary_payer_name || 'AHCCCS / DDD'}</span></div>
                <div>Diagnosis Code (ICD-10): <span className="text-slate-200 font-bold">{selectedDoc.client_diagnosis_code || selectedDoc.primary_diagnosis || selectedDoc.diagnosis || 'Unspecified'}</span></div>
                <div className="pt-1 border-t border-slate-800/60 mt-1">
                  Linked Authorization: <span className="text-sky-300 font-bold">{selectedDoc.auth_number ? `Auth #${selectedDoc.auth_number} (${selectedDoc.auth_type || 'Active'})` : (snapshot?.client?.active_auth_number ? `Auth #${snapshot.client.active_auth_number}` : 'None linked')}</span>
                </div>
                <div>Auth Validity Window: <span className="text-slate-200">{selectedDoc.auth_start_date || 'N/A'} to {selectedDoc.auth_end_date || 'N/A'}</span></div>
                <div>Remaining Units: <span className="text-slate-200">{selectedDoc.auth_available_units !== undefined && selectedDoc.auth_available_units !== null ? `${selectedDoc.auth_available_units} units available` : 'Active'} (Used: {selectedDoc.auth_units_used ?? 0} / Total: {selectedDoc.auth_total_units || 'N/A'})</span></div>
              </div>
            </div>

            {/* Treating Provider Identity Block */}
            <div className="p-3 rounded-lg bg-[#0e1626]/85 border border-slate-700/70 space-y-1.5">
              <div className="text-[10px] font-bold text-emerald-400 uppercase tracking-wider flex items-center space-x-1">
                <User className="w-3 h-3" />
                <span>Treating Provider (Rendering)</span>
              </div>
              <div className="text-white font-sans font-semibold text-xs">
                {provName}
              </div>
              <div className="text-slate-400 text-[11px] space-y-0.5">
                <div>Provider ID: <span className="text-slate-200">{selectedDoc.provider_id || 'N/A'}</span></div>
                <div>Credentials: <span className="text-slate-200">{provTitle}</span></div>
                <div>Role: <span className="text-slate-200">{provRole}</span></div>
                <div>Rendering NPI: <span className={provNpi.includes('Not Configured') ? 'text-amber-400 font-bold' : 'text-slate-200 font-bold'}>{provNpi}</span></div>
                <div>State License: <span className={provLicense.includes('Not Configured') ? 'text-amber-400 font-bold' : 'text-slate-200'}>{provLicense}</span></div>
              </div>
            </div>

            {/* Supervisory Hierarchy & Clearance Audit Block */}
            <div className="p-3 rounded-lg bg-[#0e1626]/85 border border-slate-700/70 space-y-1.5">
              <div className="text-[10px] font-bold text-indigo-400 uppercase tracking-wider flex items-center space-x-1">
                <ShieldCheck className="w-3 h-3" />
                <span>Supervisory Clearance & Hierarchy</span>
              </div>
              <div className="text-slate-400 text-[11px] space-y-0.5">
                <div>Requirement: <span className="text-slate-200">{isSelectedDocSlpa ? 'Required (SLPA Supervision Mandate)' : 'Not Required (Independent SLP)'}</span></div>
                {isSelectedDocSlpa ? (
                  <>
                    <div>Supervisor Name: <span className="text-white font-sans font-semibold">{supName}</span></div>
                    <div>Supervisor Credentials: <span className="text-slate-200">{supTitle}</span></div>
                    <div>Supervisor NPI: <span className={supNpi.includes('Not Configured') ? 'text-amber-400 font-bold' : 'text-slate-200 font-bold'}>{supNpi}</span></div>
                    <div>Supervisor License: <span className="text-slate-200">{supLicense}</span></div>
                    <div className="pt-1 border-t border-slate-800/60 mt-1">
                      Sign-off Status: <span className={selectedDoc.supervisor_approval_status === 'APPROVED' ? 'text-emerald-400 font-bold' : selectedDoc.supervisor_approval_status === 'RETURNED' ? 'text-rose-400 font-bold' : 'text-indigo-400 font-bold'}>
                        {selectedDoc.supervisor_approval_status === 'APPROVED' ? 'Approved / Co-signed' : selectedDoc.supervisor_approval_status === 'RETURNED' ? 'Returned for Revision' : 'Pending Supervisor Review'}
                      </span>
                    </div>
                    {selectedDoc.supervisor_approval_status === 'APPROVED' && (
                      <div>Co-Sign Recorded: <span className="text-slate-200">{selectedDoc.supervisor_signature || supName} • {formatConciseDateTime(selectedDoc.supervisor_signed_at)}</span></div>
                    )}
                    {selectedDoc.supervisor_approval_status === 'RETURNED' && (
                      <div className="p-1.5 rounded bg-rose-950/40 border border-rose-800/50 text-rose-300 mt-1">
                        <div className="font-bold">Returned by {supName} ({formatConciseDateTime(selectedDoc.supervisor_signed_at)}):</div>
                        <div className="italic">"{selectedDoc.supervisor_return_comments || 'Revision requested.'}"</div>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="text-slate-500 italic pt-1">Treating clinician is an independently licensed SLP. No supervisory co-signature required.</div>
                )}
              </div>
            </div>

            {/* Service Encounter & Modality Block */}
            <div className="p-3 rounded-lg bg-[#0e1626]/85 border border-slate-700/70 space-y-1.5">
              <div className="text-[10px] font-bold text-amber-400 uppercase tracking-wider flex items-center space-x-1">
                <Clock className="w-3 h-3" />
                <span>Service Encounter & Modality</span>
              </div>
              <div className="text-slate-400 text-[11px] space-y-0.5">
                <div>Service ID: <span className="text-slate-200">{selectedDoc.service_id || 'Unlinked Service'}</span></div>
                <div>
                  DOS: <span className="text-slate-200 font-bold">{selectedDoc.dos || selectedDoc.document_date || 'N/A'}</span>
                  {selectedDoc.start_time && selectedDoc.end_time ? (
                    <span> • {selectedDoc.start_time} – {selectedDoc.end_time}</span>
                  ) : (
                    <span className="text-slate-500"> • Times not recorded</span>
                  )}
                </div>
                <div>Service Code: <span className="text-slate-200 font-bold">{selectedDoc.service_code || 'Unlinked (No CPT)'}</span> (Modifiers: {selectedDoc.modifier ? `GN, ${selectedDoc.modifier}` : 'None'})</div>
                <div>Modality: <span className="text-white font-semibold">
                  {selectedDoc.place_of_service
                    ? ((selectedDoc.place_of_service === '02' || selectedDoc.place_of_service === '10' || selectedDoc.modifier === '95')
                        ? `Teletherapy (POS ${selectedDoc.place_of_service}, Modifier ${selectedDoc.modifier || '95'})`
                        : `Onsite / In-Person (POS ${selectedDoc.place_of_service})`)
                    : (selectedDoc.modifier === '95' ? 'Teletherapy (Modifier 95)' : 'Unspecified Modality')}
                </span></div>
                <div>
                  Duration: <span className="text-white font-bold">
                    {selectedDoc.actual_duration_minutes != null
                      ? `${selectedDoc.actual_duration_minutes}m actual`
                      : selectedDoc.duration_minutes != null
                      ? `${selectedDoc.duration_minutes}m actual`
                      : 'Duration not recorded (Unlinked)'}
                  </span>
                  {selectedDoc.planned_duration_minutes != null ? ` (Planned: ${selectedDoc.planned_duration_minutes}m)` : ''}
                </div>
                <div>Payroll Compliance: <span className={selectedDoc.is_qualifying_treatment ? 'text-emerald-400 font-bold' : selectedDoc.is_qualifying_treatment === false ? 'text-amber-400 font-bold' : 'text-amber-300 font-bold'}>
                  {selectedDoc.actual_duration_minutes != null || selectedDoc.duration_minutes != null
                    ? (Number(selectedDoc.actual_duration_minutes || selectedDoc.duration_minutes) >= 53
                        ? 'YES (≥53m - Weekly Ladder Eligible)'
                        : 'NON-QUALIFYING (<53m - $25 Compliance Rate & Excluded from Ladder)')
                    : 'UNLINKED ENCOUNTER (No duration recorded - pending service reconciliation)'}
                </span></div>
              </div>
            </div>
          </div>

          {/* Billing Readiness Evaluation Block */}
          <div className={`p-3.5 rounded-xl border space-y-1.5 ${
            isReady 
              ? 'bg-gradient-to-b from-emerald-950/25 via-[#0c1815]/90 to-[#08120e]/95 border-emerald-500/35 text-emerald-200 shadow-[0_2px_8px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(16,185,129,0.08)]'
              : isExcluded
              ? 'bg-gradient-to-b from-[#0c1424]/90 to-[#080d18]/95 border-slate-700/70 text-slate-400 shadow-xs'
              : 'bg-gradient-to-b from-amber-950/25 via-[#18140e]/90 to-[#120f0a]/95 border-amber-500/35 text-amber-100 shadow-[0_2px_8px_rgba(0,0,0,0.25),inset_0_1px_0_rgba(245,158,11,0.1)]'
          }`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center space-x-2">
                <span className="text-[10px] uppercase font-bold tracking-wider">Billing Readiness Evaluation:</span>
                <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                  isReady ? 'bg-emerald-900/60 text-emerald-200 border border-emerald-700/50' : 'bg-amber-900/60 text-amber-200 border border-amber-700/50'
                }`}>
                  {isReady ? 'READY FOR CLAIM GENERATION' : isExcluded ? 'EXCLUDED' : 'HELD FOR REVIEW'}
                </span>
              </div>
              <div className="text-[11px]">
                Target Route: <strong className="text-white font-sans">{targetRoute}</strong>
              </div>
            </div>
            {holdReasons.length > 0 && (
              <div className="pt-1.5 border-t border-amber-800/40 space-y-1 text-[11px] text-amber-200">
                <div className="font-bold text-amber-300">Specific Blocking Reasons ({holdReasons.length}):</div>
                <ul className="list-disc pl-4 space-y-0.5 font-sans">
                  {holdReasons.map((reason, idx) => (
                    <li key={idx}>{reason}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Canonical Organization Billing Identity Block */}
          <div className="p-3 rounded-lg bg-[#0e1626]/85 border border-slate-700/70 space-y-2">
            <div className="flex items-center space-x-2 text-sky-400 font-bold text-[10px] uppercase tracking-wider">
              <Building2 className="w-3.5 h-3.5" />
              <span>Canonical Organization Billing Identity (Source of Truth)</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5 text-[11px] text-slate-300 font-sans">
              <div>
                <span className="text-slate-500 font-mono text-[10px] block">Billing Provider / Org Name:</span>
                <strong className="text-white">{orgName}</strong>
              </div>
              <div>
                <span className="text-slate-500 font-mono text-[10px] block">Legal Entity Name:</span>
                <strong className="text-white">{legalEntityName}</strong>
              </div>
              <div>
                <span className="text-slate-500 font-mono text-[10px] block">Organization Billing NPI:</span>
                <strong className={orgNpi.includes('Not Configured') ? 'text-amber-400' : 'text-slate-200 font-mono'}>{orgNpi}</strong>
              </div>
              <div>
                <span className="text-slate-500 font-mono text-[10px] block">Tax ID (EIN):</span>
                <strong className={orgTaxId.includes('Not Configured') ? 'text-amber-400' : 'text-slate-200 font-mono'}>{orgTaxId}</strong>
              </div>
              <div>
                <span className="text-slate-500 font-mono text-[10px] block">Billing Taxonomy Code:</span>
                <strong className={orgTaxonomy.includes('Not Configured') ? 'text-amber-400' : 'text-slate-200 font-mono'}>{orgTaxonomy}</strong>
              </div>
              <div>
                <span className="text-slate-500 font-mono text-[10px] block">Canonical Billing Address:</span>
                <span className={orgAddress.includes('Not Configured') ? 'text-amber-400' : 'text-slate-200'}>{orgAddress}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
